import { ToastrService } from 'ngx-toastr';
import { isPlatformBrowser } from '@angular/common';
import { catchError, of, Subject, takeUntil } from 'rxjs';
import { Component, OnInit, Input, ChangeDetectorRef, PLATFORM_ID, Inject, OnDestroy } from '@angular/core';

import { InternalProductWithoutClassWithFavoriteStatusType } from '@sifca-monorepo/ecommerce-generator';
import { ProductClassEnum, InternalProductByClassTypeWithFavoriteStatus } from '@sifca-monorepo/ecommerce-generator';

import { AppService, AuthService, SharedService } from '../../services';
import { OrdersService } from '../../services/order/order/order.service';

@Component({
  template: '',
})
export class BaseProductGroupComponent implements OnInit, OnDestroy {
  private unsubscribeAll: Subject<any> = new Subject<any>();

  @Input() loaded = false;

  isLoading = true;
  activeTab: string;
  posId: string = '';
  isOutOfStock = true;
  isDragging: boolean;
  attrs: string[] = [];
  authenticated: boolean;
  isButtonDisabled = false;
  allProducts: InternalProductByClassTypeWithFavoriteStatus;
  products: InternalProductWithoutClassWithFavoriteStatusType[];
  bestSellers: InternalProductWithoutClassWithFavoriteStatusType[];
  titles = { promo: 'Nos Promotions', sale: 'Les plus vendus', rated: 'Notre sélection', new: 'Nouveautés', featured: 'Les vedettes' };

  constructor(
    protected appService: AppService,
    protected authService: AuthService,
    protected orderService: OrdersService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.sharedService.posId$.pipe(takeUntil(this.unsubscribeAll)).subscribe((posId) => {
      this.posId = posId;
    });
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });
    this.appService
      .getSimpleProductsByTargetGroupedByClassWithFavoriteStatus(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.appService.productsGroup$.pipe(takeUntil(this.unsubscribeAll)).subscribe((allProducts: InternalProductByClassTypeWithFavoriteStatus) => {
      if (allProducts) {
        this.attrs = [];
        this.allProducts = allProducts;
        if (this.allProducts.PROMO?.length) {
          this.attrs.push('promo');
          this.activeTab = this.titles['Nos Promotions'];
        }
        if (this.allProducts.FEATURED_PRODUCTS?.length) {
          this.attrs.push('featured');
          this.activeTab = this.titles['featured'];
        }
        if (this.allProducts?.BEST_SELLERS?.length) {
          this.bestSellers = this.allProducts?.BEST_SELLERS;
          this.attrs.push('sale');
          this.activeTab = this.titles['sale'];
        }
        if (this.allProducts.TOP_PRODUCTS?.length) {
          this.attrs.push('rated');
          this.activeTab = this.titles['rated'];
        }
        if (this.allProducts.NEW_ARRIVALS?.length) {
          this.attrs.push('new');
          this.activeTab = this.titles['new'];
        }
        this.activeTab = this.titles[this.attrs[0]] || 'Nos Promotions';

        const productMap = {
          'Nos Promotions': allProducts.PROMO,
          'Les plus vendus': allProducts.BEST_SELLERS,
          'Les vedettes': allProducts.FEATURED_PRODUCTS,
          'Notre sélection': allProducts.TOP_PRODUCTS,
          'Nouveautés': allProducts.NEW_ARRIVALS,
        };

        this.products = productMap[this.activeTab] || null;
        setTimeout(() => {
          if (isPlatformBrowser(this.platformId)) {
            window.dispatchEvent(new Event('resize'));
          }
          this.isLoading = false;
        }, 500);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  onSelect(value: string) {
    this.isLoading = true;
    let productClass: string;
    if (value === 'promo') {
      productClass = ProductClassEnum.PROMO;
    }
    if (value === 'rated') {
      productClass = ProductClassEnum.TOP_PRODUCTS;
    }
    if (value === 'featured') {
      productClass = ProductClassEnum.FEATURED_PRODUCTS;
    }
    if (value === 'new') {
      productClass = ProductClassEnum.NEW_ARRIVALS;
    }
    if (value === 'sale') {
      productClass = ProductClassEnum.BEST_SELLERS;
    }
    this.products = this.allProducts[productClass];
    setTimeout(() => {
      if (isPlatformBrowser(this.platformId)) {
        window.dispatchEvent(new Event('resize'));
      }
      this.isLoading = false;
    }, 500);
    this.changeDetectorRef.markForCheck();
  }

  draggingChange() {
    this.isDragging = true;
    setTimeout(() => {
      this.isDragging = false;
    }, 500);
  }

  addToCart(product) {
    this.isButtonDisabled = true;
    this.appService
      .getStockByTargetAndBarcode(product.barcodes[0].id)
      .pipe(
        catchError(() => {
          this.isOutOfStock = true;
          this.toastrService.error('Le produit est en rupture de stock !');
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.isOutOfStock = res.outOfStock;
          if (this.isOutOfStock === true) {
            this.toastrService.error('Le produit est en rupture de stock !');
          } else {
            if (!this.authenticated) {
              if (this.appService.itemInCart(product.barcodes[0])) {
                const value: any = {
                  barcode: product.barcodes[0],
                  price: {
                    unitGross: product.barcodes[0].price,
                    gross: product.barcodes[0].price,
                  },
                  product,
                  quantity: 1,
                };
                this.appService.updateLocalCart(value);
              } else {
                const value: any = {
                  barcode: product.barcodes[0],
                  price: {
                    unitGross: product.barcodes[0].price,
                    gross: product.barcodes[0].price,
                  },
                  product,
                  quantity: 1,
                };
                this.appService.addToLocalCart(value);
              }
              this.toastrService.success('Produit ajouté au panier.');
              this.isButtonDisabled = false;
            } else {
              const formValue = {
                barcode: product.barcodes[0].id,
                quantity: 1,
              };
              this.orderService
                .addProductsToCart([formValue])
                .pipe(
                  catchError(() => {
                    return of(null);
                  }),
                )
                .subscribe((res) => {
                  if (res) {
                    this.toastrService.success('Produit ajouté au panier.');
                    this.isButtonDisabled = false;
                  }
                });
              this.appService.loadCart();
            }
          }
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
